import { LockfileNotFoundError } from "../LockfileManager.js";
import type { ApplyOptions, ReplayService, ReplayOptions, ReplayPreparation, ReplayReport } from "../ReplayService.js";
import type { Flow } from "./Flow.js";

/**
 * `--no-replay` mode: commit the generation, set the replay-skipped marker
 * in the lockfile, return early. The next normal run sees the marker and
 * skips revert detection in pre-rebase (patches are stale-base by definition
 * and haven't been applied).
 */
export class SkipApplicationFlow implements Flow {
    constructor(private readonly service: ReplayService) {}

    async prepare(options?: ReplayOptions): Promise<ReplayPreparation> {
        const commitOpts = options
            ? {
                  cliVersion: options.cliVersion ?? "unknown",
                  generatorVersions: options.generatorVersions ?? {},
              }
            : undefined;

        await this.service.committer.commitGeneration("Update SDK (replay skipped)", commitOpts);

        // Clean up stale conflict markers from a previous crashed run.
        await this.service.cleanupStaleConflictMarkers();

        const genRecord = await this.service.committer.createGenerationRecord(commitOpts);

        let previousGenerationSha: string | null = null;
        try {
            const lock = this.service.lockManager.read();
            previousGenerationSha = lock.current_generation ?? null;
            this.service.lockManager.addGeneration(genRecord);
        } catch (error) {
            if (error instanceof LockfileNotFoundError) {
                this.service.lockManager.initializeInMemory(genRecord);
            } else {
                throw error;
            }
        }

        this.service.lockManager.setReplaySkippedAt(new Date().toISOString());

        return {
            flow: "skip-application",
            previousGenerationSha,
            currentGenerationSha: genRecord.commit_sha,
            _prepared: {
                kind: "active",
                flow: "skip-application",
                patchesToApply: [],
                newPatchIds: new Set(),
                detectorWarnings: [],
                detectorDegraded: [],
                revertedCount: 0,
                genSha: genRecord.commit_sha,
                optionsSnapshot: options,
            },
        };
    }

    async apply(_prep: ReplayPreparation, options?: ApplyOptions): Promise<ReplayReport> {
        this.service.lockManager.setFernignorePatterns(this.service.readFernignorePatterns());
        this.service.lockManager.save();
        const credentialWarnings = [...this.service.lockManager.warnings];

        // Commit the lockfile update so it's pushed with the branch.
        // Without this, the marker and generation record stay in the
        // working tree and are lost when GithubStep pushes.
        if (!options?.stageOnly) {
            await this.service.committer.commitReplay(0);
        } else {
            await this.service.committer.stageAll();
        }

        return {
            flow: "skip-application",
            patchesDetected: 0,
            patchesApplied: 0,
            patchesWithConflicts: 0,
            patchesSkipped: 0,
            conflicts: [],
            warnings: credentialWarnings.length > 0 ? credentialWarnings : undefined,
        };
    }
}
